import { useState } from 'react';
import { useFormat } from '@/hooks/useAccess';
import {
  useAccountsQuery,
  useUpdatePropertySaleMutation,
  type PropertySale,
} from '@/api/endpoints';
import { apiErrorMessage, apiFieldErrors } from '@/api/baseApi';
import { formatMoney, isoDate } from '@/lib/format';
import { Alert, Button, Input, Modal, Select, Textarea } from '@/components/ui';
import { CustomerPicker } from '@/components/CustomerPicker';

/** A date input needs YYYY-MM-DD; the API sends a full timestamp. */
const asDateInput = (value: string | null): string => (value ? isoDate(new Date(value)) : '');

/**
 * Correcting an agreement.
 *
 * What was agreed can be fixed here — who is buying, for how much, on what
 * terms, and when the next instalment is expected. What was *paid* cannot: a
 * payment that was wrong is cancelled and taken again, so the sale and the
 * cash account can never tell different stories.
 */
export function PropertySaleEditModal({
  sale,
  onClose,
  onSaved,
}: {
  sale: PropertySale;
  onClose: () => void;
  onSaved?: (sale: PropertySale) => void;
}) {
  const format = useFormat();
  const { data: accounts } = useAccountsQuery({ limit: 50 });
  const [update, state] = useUpdatePropertySaleMutation();

  const [form, setForm] = useState({
    customerId: sale.customer.id,
    price: sale.price,
    paymentPlan: sale.paymentPlan,
    accountId: sale.account?.id ?? '',
    nextDueAt: asDateInput(sale.nextDueAt),
    agreedAt: asDateInput(sale.agreedAt),
    notes: sale.notes ?? '',
  });
  const [failure, setFailure] = useState<string | null>(null);

  const fieldErrors = apiFieldErrors(state.error);
  const paid = Number(sale.paidAmount);
  const owing = Number(form.price) - paid;

  const save = async (): Promise<void> => {
    setFailure(null);
    try {
      const updated = await update({
        saleId: sale.id,
        body: {
          customerId: form.customerId,
          price: Number(form.price),
          paymentPlan: form.paymentPlan,
          ...(form.accountId ? { accountId: form.accountId } : {}),
          // An empty string is how the API is told the date is gone, so it is
          // sent rather than left out.
          nextDueAt: form.paymentPlan === 'INSTALMENTS' ? form.nextDueAt : '',
          ...(form.agreedAt ? { agreedAt: form.agreedAt } : {}),
          notes: form.notes,
        },
      }).unwrap();

      onSaved?.(updated);
      onClose();
    } catch (error) {
      setFailure(apiErrorMessage(error, 'That sale could not be changed'));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={`Edit ${sale.number}`}
      description={`${sale.property.reference} · ${formatMoney(sale.paidAmount, format)} already taken.`}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={state.isLoading}>Cancel</Button>
          <Button
            loading={state.isLoading}
            disabled={!(Number(form.price) > 0) || !form.customerId}
            onClick={() => void save()}
          >
            Save changes
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {failure && <Alert tone="danger">{failure}</Alert>}

        <CustomerPicker
          label="Buyer"
          value={form.customerId}
          onChange={(customerId) => setForm({ ...form, customerId })}
          error={fieldErrors.customerId}
          hint="The name that goes on the title."
          required
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Agreed price"
            type="number"
            min="0"
            step="0.01"
            value={form.price}
            error={fieldErrors.price}
            onChange={(event) => setForm({ ...form, price: event.target.value })}
            hint={
              owing < 0
                ? `Less than the ${formatMoney(sale.paidAmount, format)} already taken.`
                : `Leaves ${formatMoney(String(owing), format)} owing.`
            }
          />
          <Select
            label="Payment plan"
            value={form.paymentPlan}
            onChange={(event) => setForm({ ...form, paymentPlan: event.target.value as PropertySale['paymentPlan'] })}
          >
            <option value="INSTALMENTS">Instalments</option>
            <option value="OUTRIGHT">Paid outright</option>
          </Select>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {form.paymentPlan === 'INSTALMENTS' && (
            <Input
              label="Next instalment due"
              type="date"
              value={form.nextDueAt}
              onChange={(event) => setForm({ ...form, nextDueAt: event.target.value })}
              hint="Clear it to stop the reminders for this sale."
            />
          )}
          <Input
            label="Agreed on"
            type="date"
            value={form.agreedAt}
            onChange={(event) => setForm({ ...form, agreedAt: event.target.value })}
          />
        </div>

        <Select
          label="Money lands in"
          value={form.accountId}
          onChange={(event) => setForm({ ...form, accountId: event.target.value })}
          hint="Payments already taken stay where they went; this is for the next one."
        >
          <option value="">{sale.account?.name ?? 'Select an account'}</option>
          {accounts?.items
            .filter((account) => account.isActive)
            .map((account) => (
              <option key={account.id} value={account.id}>{account.name}</option>
            ))}
        </Select>

        <Textarea
          label="Notes"
          rows={2}
          value={form.notes}
          onChange={(event) => setForm({ ...form, notes: event.target.value })}
        />

        <p className="text-xs text-muted">
          A payment that was recorded wrongly is not edited here — cancel it and take it again, so
          the sale and the cash account cannot disagree.
        </p>
      </div>
    </Modal>
  );
}
